import { Link } from "react-router-dom";
import { TrendingUp, AlertTriangle, ShieldCheck } from "lucide-react";

export function TrustBar({ score }) {
    let color = "#186A3B";
    let label = "Høj tillid";
    if (score < 40) {
        color = "#BB1B2D";
        label = "Lav tillid";
    } else if (score < 65) {
        color = "#C78300";
        label = "Middel tillid";
    }
    return (
        <div className="w-full">
            <div className="flex justify-between items-baseline mb-1.5">
                <span className="text-xs uppercase tracking-wider text-[#4F5A7A] font-semibold">
                    Tillidsscore
                </span>
                <span className="text-xs font-semibold" style={{ color }}>
                    {label}
                </span>
            </div>
            <div className="w-full h-2.5 bg-[#EEF0F7] rounded-full overflow-hidden">
                <div
                    className="h-full animate-grow-bar rounded-full"
                    style={{ width: `${Math.max(2, score)}%`, backgroundColor: color }}
                />
            </div>
            <div className="mt-1 text-2xl font-serif-dm text-royal">{score}%</div>
        </div>
    );
}

export default function PartyCard({ party, stats }) {
    return (
        <Link
            to={`/partier/${party.id}`}
            className="block bg-white border border-[#E5E8F0] rounded-2xl p-6 hover-lift relative overflow-hidden"
            data-testid={`party-card-${party.short_name}`}
        >
            <div
                className="absolute top-0 left-0 w-1.5 h-full"
                style={{ backgroundColor: party.color }}
            />
            <div className="flex items-start justify-between mb-5">
                <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center font-serif-dm text-white text-2xl font-bold"
                    style={{ backgroundColor: party.color }}
                >
                    {party.short_name}
                </div>
                <span className="text-xs uppercase tracking-wider text-[#4F5A7A]">
                    {party.seats} mandater
                </span>
            </div>

            <h3 className="font-serif-dm text-xl text-royal mb-1 leading-tight">
                {party.name}
            </h3>
            <p className="text-xs text-[#4F5A7A] mb-4">
                {party.ideology} · Leder: {party.leader}
            </p>

            {stats && <TrustBar score={stats.trust_score} />}

            {stats && typeof stats.days_since_last_break !== "undefined" && (
                <div className="mt-3">
                    <DaysCounter days={stats.days_since_last_break} />
                </div>
            )}

            {stats && (
                <div className="grid grid-cols-3 gap-2 mt-5 pt-5 border-t border-[#EEF0F7]">
                    <div>
                        <div className="text-xs text-[#4F5A7A] flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" /> Holdt
                        </div>
                        <div className="font-semibold text-[#186A3B]">{stats.holdt}</div>
                    </div>
                    <div>
                        <div className="text-xs text-[#4F5A7A] flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> Brudt
                        </div>
                        <div className="font-semibold text-[#BB1B2D]">{stats.brudt}</div>
                    </div>
                    <div>
                        <div className="text-xs text-[#4F5A7A]">Afventer</div>
                        <div className="font-semibold text-[#364A7A]">{stats.afventer}</div>
                    </div>
                </div>
            )}
        </Link>
    );
}

export function DaysCounter({ days, size = "sm" }) {
    const isNone = days === null || days === undefined;
    const isGreat = !isNone && days >= 365;
    const isGood = !isNone && days >= 180 && days < 365;
    const isBad = !isNone && days < 30;

    const bg = isNone || isGreat
        ? "#DCF5E4"
        : isGood
        ? "#E8F3DB"
        : isBad
        ? "#FADDDF"
        : "#FFF2D1";
    const fg = isNone || isGreat
        ? "#186A3B"
        : isGood
        ? "#4A7A1A"
        : isBad
        ? "#8B1A1A"
        : "#7A5A00";

    const padding = size === "lg" ? "px-4 py-2.5" : "px-3 py-1.5";
    const text = size === "lg" ? "text-sm" : "text-xs";

    return (
        <div
            className={`inline-flex items-center gap-2 ${padding} rounded-full font-semibold ${text}`}
            style={{ backgroundColor: bg, color: fg }}
            title="Dage siden seneste dokumenterede løftebrud, løgn eller valgflæsk"
            data-testid="days-counter"
        >
            <ShieldCheck className={size === "lg" ? "w-4 h-4" : "w-3.5 h-3.5"} />
            {isNone ? (
                <span>Ingen registrerede brud</span>
            ) : (
                <span>
                    <strong className="font-serif-dm text-base mr-1">{days}</strong>
                    dage uden løftebrud
                </span>
            )}
        </div>
    );
}
